import { useMemo } from 'react';
import { readCatalogItems, useCatalogItems, type CatalogEntry } from './catalogItems';
import { partCatalogId } from './partIdentity';
import type { PartItem } from './partsRun';

// EVERY PART'S CATEGORY: the database's, falling back to the model's.
//
// A part's category is a fact about the part, and the database is where a part's facts live.
// The model's answer is how the part got filed in the first place and is a guess made from one
// manual's drawing; the row is what somebody has since corrected and shares with every other
// manual using the same piece.
//
// Shared by the table, which draws it, and by the badges beside the phase button, whose calls
// are sent it — a call asked with a different category than the one on screen would be asking
// about a different part.
//
// `added` is the table's own memory of rows saved since it was drawn, by part index — see
// PartsTable.
export function partCategories(
  catalog: CatalogEntry[], loaded: boolean, items: PartItem[], added?: Map<number, number>,
): string[] {
  return items.map((p, i) => {
    const id = partCatalogId(catalog, loaded, p, p.catalogId ?? added?.get(i) ?? null);
    const row = id != null ? catalog.find((c) => c.id === id) : undefined;
    return (row?.category || '').trim() || (p.category ?? '');
  });
}

export function usePartCategories(items: PartItem[], added?: Map<number, number>): string[] {
  const { items: catalog, loaded } = useCatalogItems();
  return useMemo(
    () => partCategories(catalog, loaded, items, added),
    [items, added, catalog, loaded],
  );
}

// The same answer outside a render — for the phase press, which runs after the component that
// started it may have stopped listening. A catalog that could not be read has no rows, so
// every part keeps the extraction's category: the rule above, with nothing to override it.
export const readPartCategories = async (items: PartItem[]): Promise<string[]> =>
  partCategories(await readCatalogItems(), true, items);
